import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import U2000InputDecimal from "../../../u2000Core/components/formControls/input/U2000InputDecimal";
import { U2000FormStateEnumState } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU3130TimesheetWeekView } from "./useU3130TimesheetWeekView";
import {
  U2000_TIMESHEET_WEEK_DAYS,
  u2000SumHonorariumDayTotals,
  type U2000TimesheetWeekDay,
} from "../../../u2000Core/tools/U2000TimesheetHonorariumDayTotals";
import { useU3130Context } from "../../U3130Context";
import {
  ensureBankHoursList,
  U3130_BANK_HOUR_TYPE_ORDER,
  u3130ResolveHonorariumBankBalanceDisplaySource,
  type U3130BankHourTypeId,
  type U3130BankHoursItem,
  type U3130Timesheet,
} from "../../U3130TimesheetModel";
import { u2000SkipHourBankBalanceValidationOnSubmit } from "../../../u2000Core/tools/u2000SkipHourBankBalanceValidationOnSubmit";
import { NS } from "../../U3130";
import { U3130_READONLY_TABLE_CELL_BOX_ABSENCES_HOUR_CLASS } from "../../tools/u3130ReadonlyTableCellClass";
import {
  U3130_ABSENCES_BANK_COL_WIDTH_TYPE_MIN,
  U3130_ABSENCES_BANK_DAY_HOUR_TH_CLASS,
  U3130_ABSENCES_BANK_FOOTER_LABEL_COL_CLASS,
  U3130_ABSENCES_BANK_FOOTER_TYPE_TD_CLASS,
  U3130_ABSENCES_BANK_TYPE_HEADER_TH_CLASS,
  U3130_ABSENCES_BANK_TYPE_COL_CLASS,
  U3130_ABSENCES_BANK_TYPE_LABEL_INNER_CLASS,
  U3130_ABSENCES_BANK_TYPE_TD_CLASS,
  U3130_GRID_COMPACT_DECIMAL_CONTROL_CLASS,
  U3130_ABSENCES_BANK_HOUR_FOOTER_VALUE_TD_CLASS_EDIT,
  U3130_ABSENCES_BANK_HOUR_FOOTER_VALUE_TD_CLASS_READ,
  U3130_GRID_COMPACT_ROW_CELL_PY,
  U3130_GRID_COMPACT_TABLE_CONTROL_CLASS,
  U3130_ABSENCES_BANK_COL_WIDTH_HOUR,
  U3130_ABSENCES_BANK_HOUR_BODY_TD_CLASS,
  u3130AbsencesBanquesTableMinWidth,
} from "../../tools/u3130TimesheetGridCompactStyles";
import { formatCurrency, type CurrencyFormatOptions } from "../../../u2000Core/tools/U2000CurrencyUtils";

const HORIZONTAL_ROW_TOTAL_FORMAT: CurrencyFormatOptions = {
  currencySymbol: "",
  symbolPosition: "left",
  decimalSeparator: ".",
  thousandSeparator: " ",
  allowNegative: true,
  showNegativeInParentheses: false,
  decimalDigits: 2,
};

function formatHorizontalRowTotalHours(total: number): string {
  return formatCurrency(total, HORIZONTAL_ROW_TOTAL_FORMAT);
}

function formatHonorariumDayCellHours(value: unknown): string {
  return formatCurrency(Number(value) || 0, HORIZONTAL_ROW_TOTAL_FORMAT);
}

function rowTotalHours(row: U3130BankHoursItem | undefined): number {
  if (!row) return 0;
  return U2000_TIMESHEET_WEEK_DAYS.reduce((acc, d) => acc + (Number(row[d]) || 0), 0);
}

const BANK_TYPE_I18N_KEYS: Record<U3130BankHourTypeId, string> = {
  vacation: "lblVacation",
  holidayLeave: "lblHolidayLeave",
  sickLeave: "lblSickLeave",
  authorizedLeave: "lblAuthorizedLeaveAbsence",
  special: "lblSpecialBankWithdraw",
  specialDeposit: "lblSpecialBankDeposit",
};

const U3130_ABSENCE_BANK_IDS: readonly U3130BankHourTypeId[] = ["vacation", "holidayLeave", "sickLeave", "authorizedLeave"];

function bankBalanceForType(
  bankId: U3130BankHourTypeId,
  employeeLike: { vacationBncHours?: number; holidayBncHours?: number; sickLeaveBncHours?: number; specialBncHours?: number } | null | undefined,
): number | undefined {
  if (!employeeLike) return undefined;
  switch (bankId) {
    case "vacation":
      return employeeLike.vacationBncHours;
    case "holidayLeave":
      return employeeLike.holidayBncHours;
    case "sickLeave":
      return employeeLike.sickLeaveBncHours;
    case "special":
      return employeeLike.specialBncHours;
    case "specialDeposit":
      return undefined;
    case "authorizedLeave":
      return undefined;
  }
}

interface BankHoursRowTrProps {
  index: number;
  formStateHandlers: U2000FormStateHandlers<U3130Timesheet>;
  readonly: boolean;
  bankId: U3130BankHourTypeId;
  typeLabel: string;
  balance?: number;
  vacationNegativeThresholdHours?: number;
  visibleDays: readonly U2000TimesheetWeekDay[];
}

function BankHoursRowTr({ index, formStateHandlers, readonly, bankId, typeLabel, balance, vacationNegativeThresholdHours, visibleDays }: BankHoursRowTrProps) {
  const { t } = useTranslation();
  const baseField = `bankHoursList.${index}`;
  const data = formStateHandlers.getData();
  const row = data?.bankHoursList?.[index];
  const weekTotal = rowTotalHours(row);
  const balanceNum = balance ?? 0;
  const balanceKnown = balance !== undefined && !Number.isNaN(balance);
  const negativeVacationAllowance = bankId === "vacation" ? Math.max(0, vacationNegativeThresholdHours ?? 0) : 0;
  const effectiveMaxHours = balanceNum + negativeVacationAllowance;
  const showVacationToleranceAtZero = bankId === "vacation" && balanceKnown && Math.abs(balanceNum) < 0.0001 && negativeVacationAllowance > 0;
  const reachedOrExceeded = !readonly && balanceKnown && weekTotal >= effectiveMaxHours - 0.0001;
  const exceeds = !readonly && balanceKnown && weekTotal > effectiveMaxHours + 0.0001;

  if (readonly) {
    return (
      <tr className="border-b border-gray-100 bg-amber-50/40 hover:bg-amber-50/60">
        <td className={U3130_ABSENCES_BANK_TYPE_TD_CLASS}>
          <div className={`flex flex-wrap items-center gap-x-1.5 gap-y-0.5 ${U3130_ABSENCES_BANK_TYPE_LABEL_INNER_CLASS}`}>
            <span className="font-medium">{typeLabel}</span>
            {balanceKnown ? <span className="text-xs text-gray-500">({balanceNum.toFixed(2)} h)</span> : null}
          </div>
        </td>
        {visibleDays.map((d) => (
          <td key={d} className={U3130_ABSENCES_BANK_HOUR_BODY_TD_CLASS}>
            <div className={`${U3130_READONLY_TABLE_CELL_BOX_ABSENCES_HOUR_CLASS} justify-end tabular-nums`}>{formatHonorariumDayCellHours(row?.[d])}</div>
          </td>
        ))}
        <td className={U3130_ABSENCES_BANK_HOUR_BODY_TD_CLASS}>
          <div className={`${U3130_READONLY_TABLE_CELL_BOX_ABSENCES_HOUR_CLASS} justify-end tabular-nums`}>{formatHorizontalRowTotalHours(weekTotal)}</div>
        </td>
      </tr>
    );
  }

  return (
    <tr className={`border-b border-gray-100 bg-amber-50/40 transition-colors hover:bg-amber-50/60 ${exceeds ? "ring-1 ring-inset ring-amber-500/80" : ""}`}>
      <td className={U3130_ABSENCES_BANK_TYPE_TD_CLASS}>
        <div className="flex flex-col gap-0.5">
          <div className={`flex flex-wrap items-center gap-x-1.5 gap-y-0.5 ${U3130_ABSENCES_BANK_TYPE_LABEL_INNER_CLASS}`}>
            <span className="font-medium">{typeLabel}</span>
            {balanceKnown ? <span className="text-xs text-gray-500">{t("lblBankHoursBalanceSuffix", { ns: NS, defaultValue: "Solde : {{hours}} h", hours: balanceNum.toFixed(2) })}</span> : null}
            {showVacationToleranceAtZero ? (
              <span className="text-xs font-semibold text-red-700">
                {t("lblVacationNegativeToleranceSuffix", {
                  ns: NS,
                  defaultValue: "Seuil tolere : -{{hours}} h",
                  hours: negativeVacationAllowance.toFixed(2),
                })}
              </span>
            ) : null}
          </div>
          {exceeds ? (
            <p className="text-xs text-amber-800" role="status">
              {t("msgBankHoursExceedsBalance", {
                ns: NS,
                defaultValue: "Le solde d'une des banques d'heures a été dépassé.",
              })}
            </p>
          ) : null}
        </div>
      </td>
      {visibleDays.map((d) => {
        const dayValue = Number(row?.[d]) || 0;
        const disableDayInput = reachedOrExceeded && dayValue <= 0;
        const highlightExceededDay = exceeds && dayValue > 0;
        return (
          <td key={d} className={U3130_ABSENCES_BANK_HOUR_BODY_TD_CLASS}>
            <div className={`${U3130_GRID_COMPACT_DECIMAL_CONTROL_CLASS} ${highlightExceededDay ? "[&_input]:bg-red-100/80 [&_input]:border-red-300" : ""}`}>
              <U2000InputDecimal
                formStateHandlers={formStateHandlers}
                fieldName={`${baseField}.${d}`}
                label=""
                readonly={readonly || disableDayInput}
                decimalDigits={2}
                textAlign="right"
              />
            </div>
          </td>
        );
      })}
      <td className={U3130_ABSENCES_BANK_HOUR_BODY_TD_CLASS}>
        <div className={`${U3130_READONLY_TABLE_CELL_BOX_ABSENCES_HOUR_CLASS} justify-end tabular-nums`}>{formatHorizontalRowTotalHours(weekTotal)}</div>
      </td>
    </tr>
  );
}

interface U3130AbsencesBanquesTabProps {
  formStateHandlers: U2000FormStateHandlers<U3130Timesheet>;
}

const U3130AbsencesBanquesTab: React.FC<U3130AbsencesBanquesTabProps> = ({ formStateHandlers }) => {
  const { t } = useTranslation();
  const { employee: contextEmployee, initLU } = useU3130Context();
  const legacySkipBankBalanceValidation = u2000SkipHourBankBalanceValidationOnSubmit(initLU.personalization);
  const data = formStateHandlers.getData();
  const { showWeekend, setShowWeekend, visibleDays, dayLabelsWithDates } = useU3130TimesheetWeekView();

  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ || (data != null && data.status !== "Open");

  const bankHourFooterTdClass = useMemo(
    () =>
      readonly ? U3130_ABSENCES_BANK_HOUR_FOOTER_VALUE_TD_CLASS_READ : U3130_ABSENCES_BANK_HOUR_FOOTER_VALUE_TD_CLASS_EDIT,
    [readonly],
  );
  const absencesTableMinWidth = useMemo(
    () => u3130AbsencesBanquesTableMinWidth({ dayColumnCount: visibleDays.length }),
    [visibleDays.length],
  );
  const absencesTableStyle = useMemo(
    () => ({
      width: `max(100%, ${absencesTableMinWidth})`,
      minWidth: absencesTableMinWidth,
    }),
    [absencesTableMinWidth],
  );
  const absencesTableColGroup = useMemo(
    () => (
      <colgroup>
        <col style={{ minWidth: U3130_ABSENCES_BANK_COL_WIDTH_TYPE_MIN }} />
        {visibleDays.map((d) => (
          <col key={d} style={{ width: U3130_ABSENCES_BANK_COL_WIDTH_HOUR, minWidth: U3130_ABSENCES_BANK_COL_WIDTH_HOUR }} />
        ))}
        <col style={{ width: U3130_ABSENCES_BANK_COL_WIDTH_HOUR, minWidth: U3130_ABSENCES_BANK_COL_WIDTH_HOUR }} />
      </colgroup>
    ),
    [visibleDays],
  );
  const bankCrossFooterCellClass = useMemo(() => `${bankHourFooterTdClass} text-gray-600`, [bankHourFooterTdClass]);

  const { bankBalanceSource, vacationProjectedForDisplay, vacationBankNegativeBanner } = useMemo(() => {
    if (legacySkipBankBalanceValidation) {
      const src = u3130ResolveHonorariumBankBalanceDisplaySource(data, contextEmployee);
      return {
        bankBalanceSource: src,
        vacationProjectedForDisplay: data?.vacationBalanceAfterTimesheet,
        vacationBankNegativeBanner: false,
      };
    }
    const src = u3130ResolveHonorariumBankBalanceDisplaySource(data, contextEmployee);
    if (typeof data?.vacationBalanceAfterTimesheet === "number" && Number.isFinite(data.vacationBalanceAfterTimesheet)) {
      return {
        bankBalanceSource: src,
        vacationProjectedForDisplay: data.vacationBalanceAfterTimesheet,
        vacationBankNegativeBanner: data?.vacationBankWillBeNegative === true || data.vacationBalanceAfterTimesheet < -0.0001,
      };
    }
    const vacationRow = ensureBankHoursList(data?.bankHoursList).find((b) => b.bankId === "vacation");
    const used = rowTotalHours(vacationRow);
    const opening = bankBalanceForType("vacation", src);
    if (opening !== undefined && Number.isFinite(opening)) {
      const projected = opening - used;
      return {
        bankBalanceSource: src,
        vacationProjectedForDisplay: projected,
        vacationBankNegativeBanner: projected < -0.0001 && used > 0,
      };
    }
    return {
      bankBalanceSource: src,
      vacationProjectedForDisplay: data?.vacationBalanceAfterTimesheet,
      vacationBankNegativeBanner: data?.vacationBankWillBeNegative === true,
    };
  }, [data, contextEmployee, legacySkipBankBalanceValidation]);

  const vacationNegativeThresholdHours = data?.employee?.vacationNegativeThresholdHours;
  const honorariumRowKeyPrefix = data?.id?.trim() || "draft";

  const bankRows = useMemo(() => ensureBankHoursList(data?.bankHoursList), [data?.bankHoursList]);
  const honorariaRows = useMemo(() => data?.honoraria ?? [], [data?.honoraria]);

  const absenceRows = useMemo(
    () => bankRows.filter((r) => U3130_ABSENCE_BANK_IDS.includes(r.bankId as U3130BankHourTypeId)),
    [bankRows],
  );
  const absenceFooterTotals = useMemo(() => u2000SumHonorariumDayTotals(absenceRows), [absenceRows]);
  const specialNetFooterTotals = useMemo(() => {
    const withdraw = u2000SumHonorariumDayTotals(bankRows.filter((r) => r.bankId === "special"));
    const deposit = u2000SumHonorariumDayTotals(bankRows.filter((r) => r.bankId === "specialDeposit"));
    const daySums = { ...withdraw.daySums };
    for (const d of U2000_TIMESHEET_WEEK_DAYS) {
      daySums[d] = (withdraw.daySums[d] ?? 0) - (deposit.daySums[d] ?? 0);
    }
    return { daySums, grandTotal: withdraw.grandTotal - deposit.grandTotal };
  }, [bankRows]);
  const projectFooterTotals = useMemo(() => u2000SumHonorariumDayTotals(honorariaRows), [honorariaRows]);

  useEffect(() => {
    if (data == null || data.status !== "Open") return;
    const normalized = ensureBankHoursList(data.bankHoursList);
    const cur = data.bankHoursList ?? [];
    if (normalized.length === cur.length && normalized.every((r, i) => r.bankId === cur[i]?.bankId)) {
      return;
    }
    formStateHandlers.setData({ ...data, bankHoursList: normalized } as U3130Timesheet);
  }, [data, formStateHandlers]);

  useEffect(() => {
    const currentData = formStateHandlers.getData();
    const list = ensureBankHoursList(currentData?.bankHoursList);
    let changed = false;
    const next = list.map((r) => {
      const total = rowTotalHours(r);
      if (Number((r as U3130BankHoursItem).totalHours) === total) return r;
      changed = true;
      return { ...r, totalHours: total };
    });
    if (changed) {
      formStateHandlers.setData({ ...currentData, bankHoursList: next } as U3130Timesheet);
    }
  }, [formStateHandlers, bankRows]);

  useEffect(() => {
    const current = formStateHandlers.getData();
    if (current == null) return;
    const stored = current.bankHoursFooterTotals;
    const needUpdate =
      !stored ||
      U2000_TIMESHEET_WEEK_DAYS.some((d) => (stored[d] ?? 0) !== absenceFooterTotals.daySums[d]) ||
      (stored.grandTotal ?? 0) !== absenceFooterTotals.grandTotal;
    if (needUpdate) {
      formStateHandlers.setData({
        ...current,
        bankHoursFooterTotals: {
          ...absenceFooterTotals.daySums,
          grandTotal: absenceFooterTotals.grandTotal,
        },
      });
    }
  }, [absenceFooterTotals, formStateHandlers]);

  // Hauteur naturelle (pas de chaîne flex-1 + overflow-hidden) : le tabpanel U2000
  // (overflow-y-auto) défile. Évite une grille banques à hauteur 0.
  return (
    <div className="flex min-w-0 flex-col gap-4 rounded-lg border border-gray-200 bg-muted/10 px-4 pb-2 pt-4">
      <div className="flex min-w-0 flex-col gap-2 rounded-lg border border-amber-200/80 bg-amber-50/25 p-3">
        <div className="flex w-full shrink-0 items-center gap-3">
          <p className="min-w-0 flex-1 text-xs text-gray-500">
            {t("sectionAbsencesBankHoursHint", {
              ns: NS,
              defaultValue:
                "Les soldes affichés proviennent du dossier employé lorsque votre feuille de temps est au statut Ouvert ; sinon, ce sont les soldes au moment de la période (tels qu’enregistrés sur la feuille).",
            })}
          </p>
          <label className="flex min-h-9 shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700">
            <input type="checkbox" className="h-4 w-4 shrink-0" checked={showWeekend} onChange={(e) => setShowWeekend(e.target.checked)} />
            {t("lblShowWeekendDays", { ns: NS, defaultValue: "Afficher samedi et dimanche" })}
          </label>
        </div>
        {vacationBankNegativeBanner ? (
          <div className="shrink-0 rounded-md border border-amber-300 bg-amber-100/70 px-3 py-2 text-xs text-amber-900" role="status">
            <p className="font-medium">{t("msgVacationBankWillBeNegative", { ns: NS })}</p>
            <p className="mt-1">
              {t("msgVacationProjectedBalanceAndThreshold", {
                ns: NS,
                projected: (vacationProjectedForDisplay ?? 0).toFixed(2),
                threshold: (vacationNegativeThresholdHours ?? 0).toFixed(2),
              })}
            </p>
          </div>
        ) : null}
        <div
          className={`min-w-0 max-w-full overflow-x-auto rounded-lg border border-amber-200/80 bg-amber-50/20 ${U3130_GRID_COMPACT_TABLE_CONTROL_CLASS} [&_input:not([data-u2000-skip-grid-compact])]:!px-1.5 [&_input:not([data-u2000-skip-grid-compact])]:text-sm`}
        >
          <table className="table-fixed border-collapse text-sm tabular-nums" style={absencesTableStyle}>
            {absencesTableColGroup}
            <thead className="sticky top-0 z-10 border-b border-amber-200/80 bg-amber-50/90">
              <tr>
                <th className={U3130_ABSENCES_BANK_TYPE_HEADER_TH_CLASS}>
                  {t("lblAbsenceOrBankType", { ns: NS, defaultValue: "Type" })}
                </th>
                {visibleDays.map((d) => (
                  <th key={d} className={`${U3130_ABSENCES_BANK_DAY_HOUR_TH_CLASS} font-medium text-gray-700`}>
                    {dayLabelsWithDates?.[d] ?? t(`lbl${d.charAt(0).toUpperCase()}${d.slice(1)}`, { ns: NS })}
                  </th>
                ))}
                <th className={`${U3130_ABSENCES_BANK_DAY_HOUR_TH_CLASS} font-medium text-gray-700`}>Total</th>
              </tr>
            </thead>
            <tbody>
              {U3130_BANK_HOUR_TYPE_ORDER.map((typeId, idx) => (
                <BankHoursRowTr
                  key={`${honorariumRowKeyPrefix}-${typeId}`}
                  index={idx}
                  formStateHandlers={formStateHandlers}
                  readonly={readonly}
                  bankId={typeId}
                  typeLabel={t(BANK_TYPE_I18N_KEYS[typeId], { ns: NS })}
                  balance={bankBalanceForType(typeId, bankBalanceSource ?? undefined)}
                  vacationNegativeThresholdHours={vacationNegativeThresholdHours}
                  visibleDays={visibleDays}
                />
              ))}
            </tbody>
            <tfoot className="border-t-2 border-amber-200/80 bg-amber-50/95 shadow-[0_-4px_6px_-2px_rgba(0,0,0,0.06)]">
              <tr aria-label={t("lblTotalAbsenceHours", { ns: NS, defaultValue: "Total absence" })}>
                <td className={`${U3130_ABSENCES_BANK_FOOTER_TYPE_TD_CLASS} font-semibold text-gray-800 ${U3130_ABSENCES_BANK_FOOTER_LABEL_COL_CLASS}`}>
                  {t("lblTotalAbsenceHours", { ns: NS, defaultValue: "Total absence" })}
                </td>
                {visibleDays.map((d) => (
                  <td key={d} className={`${bankHourFooterTdClass} font-semibold text-gray-800`}>
                    {(absenceFooterTotals.daySums[d] ?? 0).toFixed(2)}
                  </td>
                ))}
                <td className={`${bankHourFooterTdClass} font-bold text-gray-900`}>{absenceFooterTotals.grandTotal.toFixed(2)}</td>
              </tr>
              <tr aria-label={t("lblTotalSpecialBankNetHours", { ns: NS, defaultValue: "Total heures accumulées (net)" })}>
                <td className={`${U3130_ABSENCES_BANK_FOOTER_TYPE_TD_CLASS} font-semibold text-gray-800 ${U3130_ABSENCES_BANK_FOOTER_LABEL_COL_CLASS}`}>
                  {t("lblTotalSpecialBankNetHours", { ns: NS, defaultValue: "Total heures accumulées (net)" })}
                </td>
                {visibleDays.map((d) => (
                  <td key={d} className={`${bankHourFooterTdClass} font-semibold text-gray-800`}>
                    {(specialNetFooterTotals.daySums[d] ?? 0).toFixed(2)}
                  </td>
                ))}
                <td className={`${bankHourFooterTdClass} font-bold text-gray-900`}>{specialNetFooterTotals.grandTotal.toFixed(2)}</td>
              </tr>
              <tr aria-label={t("lblCrossTotalProjectHours", { ns: NS, defaultValue: "Total projet" })}>
                <td className={`${U3130_ABSENCES_BANK_FOOTER_TYPE_TD_CLASS} text-sm font-medium text-gray-600 ${U3130_ABSENCES_BANK_FOOTER_LABEL_COL_CLASS}`}>
                  {t("lblCrossTotalProjectHours", { ns: NS, defaultValue: "Total projet" })}
                </td>
                {visibleDays.map((d) => (
                  <td key={d} className={bankCrossFooterCellClass}>
                    {(projectFooterTotals.daySums[d] ?? 0).toFixed(2)}
                  </td>
                ))}
                <td className={`${bankCrossFooterCellClass} font-semibold`}>{projectFooterTotals.grandTotal.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default U3130AbsencesBanquesTab;
